import type { ProjectDetail } from "@esi/shared";
import { useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "../api-errors.ts";
import { changeSetPath, navigate, wikiChangesPath } from "../router.tsx";
import { useResolveChangeItem } from "./queries.ts";

/**
 * A legacy draft link (`/wiki/drafts/:id[/edit]`; a draft ID is its change item's ID): a
 * permanent redirect that replaces the URL with its change set's review, item selected. When the
 * lookup finds nothing the user may see, or fails, it lands on the wiki's review queue: never a
 * not-found or an error page. An ended session (401) or an outdated build (409) keeps the link
 * while the app offers to sign in again or reloads (api.ts), so the link still resolves after.
 */
export function LegacyDraftLink({ project, draftId }: { project: ProjectDetail; draftId: string }) {
  const found = useResolveChangeItem(project.id, draftId);
  const target = found.data
    ? changeSetPath(project.id, found.data.changeSetId, found.data.itemId)
    : found.isError && !waitsForApp(found.error)
      ? wikiChangesPath(project.id)
      : null;
  useEffect(() => {
    if (target) {
      navigate(target, { replace: true });
    }
  }, [target]);
  return <Skeleton className="h-24 w-full" />;
}

const waitsForApp = (error: Error) =>
  error instanceof ApiError && (error.status === 401 || error.status === 409);
